import {
  type CustomFieldData,
  type CustomFieldType,
  type CustomFieldValue,
  isNotDefined,
  isNotEmpty,
  unique,
} from '@terros/common'
import { type SelectItem } from '../types'
import { formatDate } from '../formatting'

export function toCustomFieldSelectOptions(options: string[], value: CustomFieldType): SelectItem[] {
  // Blank options are hidden because the server rejects them; clearing the select is how a choice is unset
  const uniqueOptions = unique(options.filter(isNotEmpty))
  const selectItems: SelectItem[] = uniqueOptions.map((option) => ({ label: option, value: option }))
  if (typeof value === 'string' && !selectItems.some((item) => item.value === value)) {
    selectItems.push({ label: value, value, disabled: true })
  }
  return selectItems
}

// A field that allows multiple values may still hold a single value written before, or by a client that sends one.
export function toCustomFieldMultiSelectValue(value: CustomFieldValue | undefined): string[] {
  if (isNotDefined(value)) return []
  const values = Array.isArray(value) ? value : [value]
  return values.filter((item): item is string => typeof item === 'string' && isNotEmpty(item))
}

export function toCustomFieldMultiSelectOptions(options: string[], values: string[]): SelectItem[] {
  const selectItems: SelectItem[] = unique(options.filter(isNotEmpty)).map((option) => ({
    label: option,
    value: option,
  }))
  // Stale values stay removable: once the list changes the server validates every item, so a removed option would block saving
  const staleValues = values.filter((value) => !selectItems.some((item) => item.value === value))
  return [...selectItems, ...staleValues.map((value) => ({ label: value, value }))]
}

export const stringifyCustomFieldValue = (customField: CustomFieldData, value: string | number | boolean): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (customField.fieldType === 'date' && typeof value === 'number') return formatDate(value)
  return value.toString()
}
